import { ref } from 'vue'

import {
  useDeactivateAccountMutation,
  useDeleteAccountMutation,
} from '@/shared/graphql/legacy/generated'
import { getStripe } from '@/shared/services/stripe'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { useWebviewStore } from '@/shared/stores/webview'
import { openModal, Modals } from '@/shared/dialogs/modals'

import { closureContent, type ClosureMode } from './content'

export function useAccountClosure(mode: ClosureMode) {
  const toastStore = useToastStore()
  const webviewStore = useWebviewStore()

  const { mutate: deactivateAccount } = useDeactivateAccountMutation()
  const { mutate: deleteAccount } = useDeleteAccountMutation()

  const submitting = ref(false)

  const authenticatePayment = async (clientSecret: string) => {
    if (webviewStore.isActive) return true

    const stripe = await getStripe()
    if (!stripe) return true

    const { error } = await stripe.confirmCardPayment(clientSecret)
    if (error) {
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Payment authentication failed',
        error: new Error(error.message),
      })
      return false
    }

    return true
  }

  const submit = async () => {
    submitting.value = true

    try {
      const requiresAction =
        mode === 'deactivate'
          ? (await deactivateAccount())?.data?.deactiveAccount.requiresAction
          : (await deleteAccount())?.data?.deleteAccount.requiresAction

      if (requiresAction?.clientSecret) {
        const authenticated = await authenticatePayment(
          requiresAction.clientSecret
        )
        if (!authenticated) {
          submitting.value = false
          return
        }
      }

      const { success } = closureContent[mode]

      openModal(Modals.AccountClosed, {
        props: {
          title: success.title,
          body: success.body,
          clearStorage: mode === 'deactivate',
        },
      })
    } catch (error) {
      submitting.value = false
      toastStore.showToast({
        type: ToastType.Error,
        content: 'Something went wrong. Please try again.',
        error: error as Error,
      })
    }
  }

  return { submitting, submit }
}
